/* Melora Archive — the house sections' motion and the campaign rail.
   Content is visible by default; script only opts it into a small rise that
   settles. Nothing bounces, and nothing moves under reduced motion or in the
   theme editor, where sections re-render mid-animation. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('melora-reveal')) {
    customElements.define('melora-reveal', class extends HTMLElement {
      connectedCallback() {
        if (reduced.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            this.observer.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

        this.querySelectorAll('[data-reveal-item]').forEach((item, index) => {
          item.classList.add('ma-reveal');
          // stagger within a row, never across the whole page
          item.style.setProperty('--reveal-delay', `${(index % 4) * 90}ms`);
          this.observer.observe(item);
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    });
  }

  if (!customElements.get('melora-rail')) {
    customElements.define('melora-rail', class extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.ma-rail');
        if (!this.rail) return;

        this.prev = this.querySelector('[data-rail="prev"]');
        this.next = this.querySelector('[data-rail="next"]');
        this.sync = this.sync.bind(this);

        this.bindDrag();
        this.rail.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') { this.scrollByStep(1); event.preventDefault(); }
          if (event.key === 'ArrowLeft') { this.scrollByStep(-1); event.preventDefault(); }
        });
        this.prev?.addEventListener('click', () => this.scrollByStep(-1));
        this.next?.addEventListener('click', () => this.scrollByStep(1));

        this.rail.addEventListener('scroll', this.sync, { passive: true });
        this.resizeObserver = new ResizeObserver(this.sync);
        this.resizeObserver.observe(this.rail);

        // selecting an image block in the theme editor brings it into view
        this.addEventListener('shopify:block:select', (event) => {
          event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });

        this.sync();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      step() {
        const first = this.rail.querySelector('li');
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 20;
        return first ? first.getBoundingClientRect().width + gap : 320;
      }

      scrollByStep(direction) {
        this.rail.scrollBy({ left: direction * this.step(), behavior: reduced.matches ? 'auto' : 'smooth' });
      }

      sync() {
        const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
      }

      bindDrag() {
        const rail = this.rail;
        let down = false, startX = 0, startLeft = 0, moved = 0;

        rail.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return; // native scrolling is better
          down = true; moved = 0;
          startX = event.clientX; startLeft = rail.scrollLeft;
          rail.setPointerCapture(event.pointerId);
          rail.classList.add('is-dragging');
        });
        rail.addEventListener('pointermove', (event) => {
          if (!down) return;
          const dx = event.clientX - startX;
          moved = Math.max(moved, Math.abs(dx));
          rail.scrollLeft = startLeft - dx;
        });
        ['pointerup', 'pointercancel'].forEach((type) => {
          rail.addEventListener(type, () => { down = false; rail.classList.remove('is-dragging'); });
        });
        rail.addEventListener('click', (event) => { if (moved > 6) event.preventDefault(); }, true);
        rail.addEventListener('dragstart', (event) => event.preventDefault());
      }
    });
  }

  // The Collections, as an index: the names drive the photograph and the pieces
  if (!customElements.get('melora-collection-index')) {
    customElements.define('melora-collection-index', class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[role="tablist"]');
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        if (!this.list || !this.tabs.length) return;

        this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        this.covers = [...this.querySelectorAll('.ma-cx__cover')];
        this.stage = this.querySelector('.ma-cx__stage');
        this.current = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          if (!this.hasAttribute('data-hover')) return;
          tab.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse') return;
            clearTimeout(this.intent);
            this.intent = setTimeout(() => this.select(index), 90); // passing over a name is not choosing it
          });
          tab.addEventListener('pointerleave', () => clearTimeout(this.intent));
        });

        this.list.addEventListener('keydown', (event) => {
          const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
          if (step) this.select(this.current + step, true);
          else if (event.key === 'Home') this.select(0, true);
          else if (event.key === 'End') this.select(this.tabs.length - 1, true);
          else return;
          event.preventDefault();
        });

        // swipe the photograph to move through the collections
        let startX = null;
        this.stage?.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') startX = event.clientX; });
        this.stage?.addEventListener('pointerup', (event) => {
          if (startX === null) return;
          const dx = event.clientX - startX;
          startX = null;
          if (Math.abs(dx) > 40) this.select(this.current + (dx < 0 ? 1 : -1));
        });
        this.stage?.addEventListener('pointercancel', () => { startX = null; });

        // selecting a collection block in the theme editor shows it
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
          if (index > -1) this.select(index);
        });
      }

      disconnectedCallback() {
        clearTimeout(this.intent);
      }

      select(index, focus = false) {
        index = (index + this.tabs.length) % this.tabs.length;

        if (index !== this.current) {
          this.tabs.forEach((tab, k) => {
            const on = k === index;
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
            tab.tabIndex = on ? 0 : -1;
            this.panels[k]?.classList.toggle('is-active', on);
            this.covers[k]?.classList.toggle('is-active', on);
            this.covers[k]?.toggleAttribute('aria-hidden', !on);
          });
          this.current = index;

          // on phones the names scroll sideways; keep the chosen one in view
          if (this.list.scrollWidth > this.list.clientWidth) {
            const tabRect = this.tabs[index].getBoundingClientRect();
            const listRect = this.list.getBoundingClientRect();
            this.list.scrollTo({
              left: this.list.scrollLeft + (tabRect.left - listRect.left) - (listRect.width - tabRect.width) / 2,
              behavior: reduced.matches ? 'auto' : 'smooth'
            });
          }
        }

        if (focus) this.tabs[index].focus();
      }
    });
  }

  // The Collections, as tall panels: the panel in hand widens
  if (!customElements.get('melora-collection-panels')) {
    customElements.define('melora-collection-panels', class extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('.ma-cp__item')];

        this.items.forEach((item) => {
          item.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse') return;
            clearTimeout(this.intent);
            this.intent = setTimeout(() => this.open(item), 80);
          });
          item.addEventListener('pointerleave', () => clearTimeout(this.intent));
          item.querySelector('a')?.addEventListener('focus', () => this.open(item));
        });

        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('.ma-cp__item');
          if (!item) return;
          this.open(item);
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        });
      }

      disconnectedCallback() {
        clearTimeout(this.intent);
      }

      open(item) {
        this.items.forEach((other) => other.classList.toggle('is-active', other === item));
      }
    });
  }
})();
